import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import vehicleService from '../../services/vehicleService.js';
import { Users, ArrowLeft } from 'lucide-react';
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const AddVehicleOwner = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    owner_name: '',
    contact: '',
    email: '',
    address: '',
    gst: '',
    vehicles: [],
    owner_percentage: ''
  });
  const [vehicles, setVehicles] = useState([]);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    owner_name:       (v) => validators.required(v, 'Owner name') || validators.textOnly(v, 'Owner name'),
    contact:          (v) => validators.required(v, 'Contact') || validators.contact(v),
    email:            (v) => v ? validators.email(v) : '',
    gst:              (v) => v ? validators.gst(v) : '',
    owner_percentage: (v) => validators.number(v, { min: 0, max: 100, label: 'Owner percentage' }),
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setDataLoading(true);
      const vehiclesRes = await vehicleService.getAllVehicles(1, 1000, { all: true });
      const vehicleData = vehiclesRes.data?.data || vehiclesRes.data || [];
      setVehicles(Array.isArray(vehicleData) ? vehicleData : []);
    } catch (err) {
      Alert.error('Failed to load form data');
      console.error('fetchInitialData error:', err);
    } finally {
      setDataLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'contact') filtered = inputFilters.digits10(value);
    else if (name === 'owner_name') filtered = inputFilters.lettersOnly(value);
    else if (name === 'owner_percentage') filtered = inputFilters.amount(value);
    else if (name === 'gst') filtered = value.toUpperCase();

    setFormData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleVehicleToggle = (vehicleId) => {
    setFormData(prev => {
      const id = Number(vehicleId);
      const already = prev.vehicles.includes(id);
      return {
        ...prev,
        vehicles: already
          ? prev.vehicles.filter(v => v !== id)
          : [...prev.vehicles, id]
      };
    });
  };

  const handleSelectAllVehicles = () => {
    const allIds = vehicles.map(v => v.id);
    const allSelected = allIds.every(id => formData.vehicles.includes(id));
    setFormData(prev => ({
      ...prev,
      vehicles: allSelected ? [] : allIds
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    const payload = {
      ...formData,
      owner_percentage: formData.owner_percentage !== '' ? Number(formData.owner_percentage) : null
    };

    setLoading(true);
    try {
      await vehicleOwnerService.create(payload);
      Alert.success('Vehicle owner created successfully!');
      setTimeout(() => navigate('/vehicle-owners'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create vehicle owner');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
            <Users className="w-6 h-6 text-secondary shrink-0" /> Add Vehicle Owner
          </h1>
          <button
            type="button"
            onClick={() => navigate('/vehicle-owners')}
            className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt hover:bg-buttonBg border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap shrink-0"
          >
            <span className="group-hover:-translate-x-1 transition-transform"><ArrowLeft className="w-4 h-4" /></span>
            Back to Owners
          </button>
        </div>

        {dataLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading form data..." />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                          Owner Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="owner_name"
                          value={formData.owner_name}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.owner_name ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.owner_name} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                          Contact <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="tel"
                          name="contact"
                          value={formData.contact}
                          onChange={handleChange}
                          placeholder="Enter 10-digit number"
                          maxLength={10}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.contact ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.contact} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.email} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                          GST Number
                        </label>
                        <input
                          type="text"
                          name="gst"
                          value={formData.gst}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.gst ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.gst} />
                      </div>

                      <div className="col-span-2 md:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                          Address
                        </label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div className="col-span-2 md:col-span-2">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary">
                            Vehicles
                          </label>
                          {vehicles.length > 0 && (
                            <button
                              type="button"
                              onClick={handleSelectAllVehicles}
                              className="text-xs text-orange-500 hover:text-orange-600 font-medium"
                            >
                              {vehicles.every(v => formData.vehicles.includes(v.id)) ? 'Deselect All' : 'Select All'}
                            </button>
                          )}
                        </div>
                        {vehicles.length > 0 && (
                          <input
                            type="text"
                            placeholder="Search vehicles..."
                            value={vehicleSearch}
                            onChange={(e) => setVehicleSearch(e.target.value)}
                            className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                        )}
                        {vehicles.length > 0 ? (
                          <div className="border border-gray-300 rounded-xl max-h-48 overflow-y-auto divide-y divide-gray-100">
                            {vehicles
                              .filter(v => {
                                if (!vehicleSearch) return true;
                                const q = vehicleSearch.toLowerCase();
                                return (v.vehicle_number || '').toLowerCase().includes(q) || (v.vehicle_name || '').toLowerCase().includes(q);
                              })
                              .map((vehicle) => (
                              <label
                                key={vehicle.id}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-orange-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.vehicles.includes(vehicle.id)}
                                  onChange={() => handleVehicleToggle(vehicle.id)}
                                  className="accent-blue-500"
                                />
                                <span className="text-sm text-secondary">
                                  {vehicle.vehicle_number} — {vehicle.vehicle_name}
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 border border-gray-200 rounded-xl p-3">No vehicles available</p>
                        )}
                        {formData.vehicles.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">{formData.vehicles.length} vehicle{formData.vehicles.length > 1 ? 's' : ''} selected</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                          Owner Percentage (%)
                        </label>
                        <input
                          type="text"
                          name="owner_percentage"
                          value={formData.owner_percentage}
                          onChange={handleChange}
                          placeholder="e.g. 100"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.owner_percentage ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.owner_percentage} />
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                      <Button variant="primary" type="submit" disabled={loading} loading={loading}>
                        {loading ? 'Creating...' : 'Create Owner'}
                      </Button>
                      <Button variant="secondary" type="button" onClick={() => navigate('/vehicle-owners')}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
    </DashboardLayout>
  );
};

export default AddVehicleOwner;
